'use client';
import React, { useState, useEffect, useRef } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  productType: string;
  quantity: string;
  message: string;
}

const productTypes = [
  'Ecom Envelopes',
  'Ecom Boxes',
  'Tapes & Tags',
  'Stickers',
  'Pouches',
  'Thank You / Business Cards',
  'Glass Jars',
  'Honeycomb Paper',
  'Butter / Wrapping Paper',
  'Cotton Clothing Labels',
  'Monocartons',
  'Paper Carry Bags',
  'Cotton Tote Bags',
  'Running Bags',
  'Saree Bags',
  'Drawstring Bags',
  'Conference Kit (Full)',
  'Kraft Paper Folders',
  'Cotton Lanyards',
  'Seed Paper Products',
  'Seed Pens',
  'Kraft Certificates',
  'Other / Custom',
];

export default function ContactSection() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    productType: '',
    quantity: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section ref={sectionRef} className="py-12 lg:py-20 bg-primary/5 green-pattern" id="contact">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start transition-all duration-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Left: Info */}
          <div>
            <span className="inline-block bg-primary text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
              Work With Us
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-dark mb-4"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Let's create your perfect packaging
            </h2>
            <p className="text-gray-600 font-normal text-base mb-8 leading-relaxed">
              Tell us about your packaging needs and we'll get back to you within 24 hours
              with a custom quote tailored to your brand.
            </p>

            <div className="space-y-4">
              {[
                { emoji: '📞', title: 'Call Us', detail: '+91 91102 72914' },
                { emoji: '📧', title: 'Email Us', detail: 'info@ecosange.com' },
                { emoji: '📍', title: 'Visit Us', detail: 'TBS Workspace, 2nd Floor, Patia, Bhubaneswar, Odisha – 751024' },
                { emoji: '⏰', title: 'Response Time', detail: 'Within 24 hours' },
              ].map((item) => (
                <div key={item.title} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">{item.emoji}</span>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{item.title}</div>
                    <div className="text-sm font-semibold text-gray-800">{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Shipping Info</p>
              <p className="text-sm text-gray-600 font-normal">Standard products ship in <strong>3–5 business days</strong>. Custom orders are typically ready in <strong>2–3 weeks</strong>.</p>
              <p className="text-xs text-gray-400 mt-2 font-normal">* MOQ: 100 units for standard products. Custom product MOQ may vary.</p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-xl border border-gray-100">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3
                  className="text-2xl font-bold text-primary-dark mb-2"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  Thank you!
                </h3>
                <p className="text-gray-500 font-normal">
                  We've received your enquiry and will get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-primary mt-6"
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h3
                  className="text-xl font-bold text-primary-dark mb-4"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  Get a Custom Quote
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-normal text-gray-800 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-normal text-gray-800 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-normal text-gray-800 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your brand name"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-normal text-gray-800 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Product Type *</label>
                  <select
                    name="productType"
                    value={formData.productType}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-normal text-gray-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                  >
                    <option value="">Select a product type</option>
                    {productTypes.map((pt) => (
                      <option key={pt} value={pt}>{pt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Estimated Quantity</label>
                  <input
                    type="text"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="e.g. 500 units"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-normal text-gray-800 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Tell us about your packaging requirements, size, print details, etc."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-normal text-gray-800 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all resize-none"
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600 font-normal">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full text-base disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending…' : 'Send Enquiry →'}
                </button>

                <p className="text-xs text-gray-400 text-center font-normal">
                  We respond within 24 hours. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
